import React, { useEffect, useState } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { Button, Tag, Tooltip as AntTooltip } from "antd";
import { AgentService } from "../../services/AgentService";

interface AgentStatusProps {
  agentId: number;
  apiKey: ApiKeyOrGetter;
}

const AgentStatus: React.FC<AgentStatusProps> = ({ agentId, apiKey }) => {
  const [deploymentStatus, setDeploymentStatus] = useState<Record<
    string,
    string
  > | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [deployLoading, setDeployLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!agentId) return;

    setLoading(true);
    AgentService.getAgentDeploymentStatus(agentId, () => {}, apiKey).subscribe(
      (response: any) => {
        setDeploymentStatus(response.statuses || {});
        setLoading(false);
      },
      (error: any) => {
        console.error("Error fetching deployment status:", error);
        setDeploymentStatus(null);
        setLoading(false);
      }
    );
  }, [agentId, apiKey]);

  if (loading) {
    return <div className="h-6 bg-muted rounded w-32 animate-pulse" />;
  }

  if (!deploymentStatus) {
    return (
      <span className="text-sm text-muted-foreground italic">
        Status unavailable
      </span>
    );
  }

  const deployedCount = Object.values(deploymentStatus).filter(
    (s) => s === "READY"
  ).length;
  const totalRegions = Object.keys(deploymentStatus).length;
  const someDeploying = Object.values(deploymentStatus).some(
    (s) => s === "DEPLOYING"
  );
  const hasAnyDeployed = deployedCount > 0;

  const refreshStatus = () => {
    setLoading(true);
    AgentService.getAgentDeploymentStatus(agentId, () => {}, apiKey).subscribe(
      (response: any) => {
        setDeploymentStatus(response.statuses || {});
        setLoading(false);
      },
      () => {
        setDeploymentStatus(null);
        setLoading(false);
      }
    );
  };

  const handleDeploy = () => {
    setDeployLoading(true);
    AgentService.ensureAgentReadyGlobal(agentId, () => {}, apiKey).subscribe(
      () => {
        setDeployLoading(false);
        // After triggering deploy, refresh status
        refreshStatus();
      },
      () => {
        setDeployLoading(false);
      }
    );
  };

  const tooltipContent = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span>{`${deployedCount}/${totalRegions} available regions deployed`}</span>
      {!hasAnyDeployed && !someDeploying && (
        <Button
          size="small"
          type="primary"
          loading={deployLoading}
          onClick={handleDeploy}>
          Deploy
        </Button>
      )}
    </div>
  );

  let tagText = "NOT DEPLOYED";
  let tagColor: "success" | "warning" | "error" | "default" = "default";
  if (hasAnyDeployed) {
    tagText = "DEPLOYED";
    tagColor = "success";
  } else if (someDeploying) {
    tagText = "DEPLOYING";
    tagColor = "warning";
  }
  return (
    <AntTooltip title={tooltipContent}>
      <Tag color={tagColor} bordered>
        {tagText}
      </Tag>
    </AntTooltip>
  );
};

export default AgentStatus;
